import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import api from "../lib/api";
import { Trophy, Sparkles, PartyPopper, ArrowRight } from "lucide-react";

const roleClass = (r) => r === "creator" ? "text-nexus-pink" : "text-nexus-blue";

export default function WinCard() {
  const { drawId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/raffle/win/${drawId}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [drawId]);

  if (notFound) {
    return (
      <div className="min-h-screen grain flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">404</div>
          <h1 className="font-display font-bold text-3xl">This winner card doesn&apos;t exist</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-nexus-blue hover:text-white">
            Go to Nexus <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grain flex items-center justify-center text-zinc-500 animate-pulse">Loading…</div>;

  const w = data.winner || {};
  const shareTitle = `${w.name} just won ${data.prize} on Nexus 🎉`;
  const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/raffle/win/${data.id}/og.png`;

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <SEO
        title={shareTitle}
        description={`Congratulations to ${w.name} — winner of ${data.prize} at the Nexus launch raffle.`}
        path={`/win/${data.id}`}
        image={ogImage}
      />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-nexus-pink/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-nexus-blue/20 blur-3xl pointer-events-none" />

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 relative">
        <Link to="/" className="inline-block mb-8 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-white transition-colors">
          ← Nexus · collabnexus.co.uk
        </Link>

        <div className="glass rounded-3xl p-8 md:p-12 border-2 border-nexus-pink/30 relative overflow-hidden" data-testid="win-card">
          <div className="absolute top-6 right-6 opacity-20">
            <PartyPopper className="h-24 w-24 text-nexus-pink" />
          </div>

          <div className="text-xs uppercase tracking-[0.3em] text-nexus-pink mb-4 flex items-center gap-2 font-semibold">
            <Trophy className="h-3.5 w-3.5" /> Nexus launch raffle · winner
          </div>

          <div className="mb-6">
            <div className="text-sm text-zinc-400 mb-1">Congratulations to</div>
            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter nexus-gradient-text leading-[0.95]" data-testid="win-name">
              {w.name}
            </h1>
            <div className={`mt-3 text-sm uppercase tracking-widest font-semibold ${roleClass(w.role)}`}>
              {w.role}{w.company ? ` · ${w.company}` : ""}{w.location ? ` · ${w.location}` : ""}
            </div>
          </div>

          {w.tagline && (
            <p className="text-lg text-zinc-200 italic border-l-2 border-nexus-pink/40 pl-4 my-6">
              &ldquo;{w.tagline}&rdquo;
            </p>
          )}

          {(w.niches || []).length > 0 && (
            <div className="flex gap-1.5 flex-wrap mb-8">
              {w.niches.slice(0, 6).map((n) => (
                <span key={n} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">{n}</span>
              ))}
            </div>
          )}

          <div className="p-6 rounded-2xl nexus-gradient text-white mt-6" data-testid="win-prize">
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Wins</div>
            <div className="font-display font-black text-3xl md:text-4xl mt-1">{data.prize}</div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded-xl bg-white/[0.02] hairline">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Pool</div>
              <div className="font-semibold mt-0.5 capitalize">{data.pool} — {data.pool_size} entrants</div>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.02] hairline">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Drawn</div>
              <div className="font-semibold mt-0.5">{new Date(data.drawn_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.02] hairline">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Odds</div>
              <div className="font-semibold mt-0.5">1 in {data.pool_size}</div>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <Link to="/auth?mode=signup" className="nexus-gradient text-white rounded-full px-5 py-3 font-semibold text-sm hover:opacity-90" data-testid="win-signup-cta">
              Join Nexus <Sparkles className="h-4 w-4 ml-1.5 inline" />
            </Link>
            <Link to="/discover" className="rounded-full px-5 py-3 border border-white/10 text-white hover:bg-white/5 text-sm">
              Browse creators
            </Link>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-zinc-500">
          Screenshot this page and share it on social — or copy the URL from your browser bar.
        </div>
      </div>
    </div>
  );
}
